import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type CreateReconcileSessionParams,
  type CreateReconcileStatementParams,
  type IReconcileSessionRepository,
  type ListSessionMatchesParams,
  type ListSessionMatchesResult,
  type SubmitCreditCardBillPayload,
} from '@webapp/modules/accounts-payable/business-logic/interfaces/IReconcileSessionRepository'
import { type ReconcileSessionConstructor } from '@webapp/modules/accounts-payable/entities/ReconcileSession'
import { type SessionApproverChoiceConstructor } from '@webapp/modules/accounts-payable/entities/SessionApproverChoice'
import { type ReconcileMatchApiModel } from '../../api-models/ReconcileMatchApiModel'
import { type ReconcileSessionApiModel } from '../../api-models/ReconcileSessionApiModel'
import { type SessionApproverChoiceApiModel } from '../../api-models/SessionApproverChoiceApiModel'
import { mapReconcileMatchApiModel } from '../../mappers/mapReconcileMatchApiModel'
import { mapReconcileSessionApiModel } from '../../mappers/mapReconcileSessionApiModel'
import { mapSessionApproverChoiceApiModel } from '../../mappers/mapSessionApproverChoiceApiModel'

interface Dependency {
  httpClient: IHttpClient
}

interface ApiPaginationApiModel {
  count?: number
  page_size?: number
  num_pages?: number
  current_page?: number
}

interface ListSessionMatchesResponse {
  data: ReconcileMatchApiModel[]
  metadata?: {
    summary?: { total_amount?: number }
    pagination?: ApiPaginationApiModel
  }
}

const mapPagination = (
  pagination: ApiPaginationApiModel | undefined
): RepoPaginationResponse => ({
  count: pagination?.count ?? 0,
  pageSize: pagination?.page_size ?? 10,
  numPages: pagination?.num_pages ?? 1,
  page: pagination?.current_page ?? 1,
})

export class ReconcileSessionRepository implements IReconcileSessionRepository {
  constructor(private readonly deps: Dependency) {}

  async listMatches(
    sessionId: number,
    params: ListSessionMatchesParams
  ): Promise<ListSessionMatchesResult> {
    try {
      const res = await this.deps.httpClient.get<ListSessionMatchesResponse>(
        `/api/v3/reconcile/sessions/${sessionId}/matches/`,
        {
          params: {
            page: params.page ?? 1,
            page_size: params.pageSize ?? 10,
          },
        }
      )

      return {
        matches: res.data.data.map(mapReconcileMatchApiModel),
        totalAmount: res.data.metadata?.summary?.total_amount ?? null,
        pagination: mapPagination(res.data.metadata?.pagination),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async updateSession(
    sessionId: number,
    params: { balance?: string; statementDate?: string }
  ): Promise<ReconcileSessionConstructor> {
    try {
      const res = await this.deps.httpClient.put<{
        data: ReconcileSessionApiModel
      }>(`/api/v3/reconcile/sessions/${sessionId}/`, {
        ...(params.balance !== undefined ? { balance: params.balance } : {}),
        ...(params.statementDate !== undefined
          ? { statement_date: params.statementDate }
          : {}),
      })

      return mapReconcileSessionApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async createMatch(payload: {
    sessionId: number
    creditcardItemIds: number[]
    statementItemIds: number[]
  }): Promise<void> {
    try {
      await this.deps.httpClient.post(`/api/v3/reconcile/matches/`, {
        session: payload.sessionId,
        creditcard_items: payload.creditcardItemIds,
        statement_items: payload.statementItemIds,
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async deleteMatch(matchId: number): Promise<void> {
    try {
      // ModelViewSet detail route, 204 (plan Open Questions assumption).
      await this.deps.httpClient.delete(`/api/v3/reconcile/matches/${matchId}/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async createSession(
    params: CreateReconcileSessionParams
  ): Promise<ReconcileSessionConstructor> {
    try {
      const res = await this.deps.httpClient.post<{
        data: ReconcileSessionApiModel
      }>(`/api/v3/reconcile/sessions/`, {
        creditcard: params.creditcardId,
        ...(params.balance ? { balance: params.balance } : {}),
        statement_date: params.statementDate,
      })

      return mapReconcileSessionApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async createStatement(params: CreateReconcileStatementParams): Promise<void> {
    try {
      await this.deps.httpClient.post(`/api/v3/reconcile/statements/`, {
        creditcard: params.creditcardId,
        attachment: params.attachmentId,
        attachment_type: params.attachmentType,
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getApproverChoices(
    sessionId: number,
    approvalChainId: number
  ): Promise<SessionApproverChoiceConstructor[]> {
    try {
      const res = await this.deps.httpClient.get<{
        data: SessionApproverChoiceApiModel[]
      }>(`/api/v3/reconcile/sessions/${sessionId}/approver-choices/`, {
        params: { approval_chain: approvalChainId },
      })

      return res.data.data.map(mapSessionApproverChoiceApiModel)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async submitBill(
    sessionId: number,
    payload: SubmitCreditCardBillPayload
  ): Promise<ReconcileSessionConstructor> {
    try {
      const res = await this.deps.httpClient.put<{
        data: ReconcileSessionApiModel
      }>(`/api/v3/reconcile/sessions/${sessionId}/`, payload)

      return mapReconcileSessionApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
